import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  Avatar
} from '@mui/material';
import { ArrowBack, CheckCircle, Refresh, Description } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useForm } from '../hooks/useForm';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  getLocalizedName,
  hasGroupedDocuments,
  getGroupedDocuments
} from '../models/FormItem';
import { checklistService } from '../services/checklist.service';

export const ChecklistPage: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const { data: form, isLoading, error } = useForm(formId || '');

  // State for grouped documents - store selected doc indices per group (Set for multi-select)
  const [selectedGroupIndices, setSelectedGroupIndices] = useState<Set<number>[]>([]);

  // State for flat documents (checkbox mode)
  const [checkedDocuments, setCheckedDocuments] = useState<boolean[]>([]);

  // Completion dialog
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);

  const isGrouped = form ? hasGroupedDocuments(form, currentLanguage) : false;
  const groupedDocs = isGrouped && form ? getGroupedDocuments(form, currentLanguage) : [];

  // Scroll to top on mount to prevent auto-scroll
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get flat documents
  const getFlatDocuments = (): string[] => {
    if (!form || isGrouped) return [];
    const raw = form._rawRequiredDocuments;
    if (!raw) return form.requiredDocuments || [];
    
    const langData = raw[currentLanguage] || raw;
    if (Array.isArray(langData) && typeof langData[0] === 'string') {
      return langData;
    }
    return form.requiredDocuments || [];
  };

  const flatDocuments = getFlatDocuments();

  // Initialize state
  useEffect(() => {
    if (!form) return;

    const loadState = async () => {
      const saved = await checklistService.loadChecklist(formId || '');
      
      if (isGrouped) {
        const initialState: Set<number>[] = Array.from({ length: groupedDocs.length }, () => new Set<number>());
        if (saved && saved.selectedOptions) {
          // Restore from saved state - convert Map to Set array
          const restored: Set<number>[] = [];
          for (let i = 0; i < groupedDocs.length; i++) {
            const savedValue = saved.selectedOptions.get(i.toString());
            if (savedValue) {
              // Parse comma-separated indices into Set
              const indices = savedValue.split(',').map(s => parseInt(s)).filter(n => !isNaN(n));
              restored.push(new Set(indices));
            } else {
              restored.push(new Set());
            }
          }
          setSelectedGroupIndices(restored.length === groupedDocs.length ? restored : initialState);
        } else {
          setSelectedGroupIndices(initialState);
        }
      } else {
        const initialState = new Array(flatDocuments.length).fill(false);
        if (saved) {
          // Restore from saved state
          const savedSet = saved.selectedDocuments;
          const restored = flatDocuments.map((_, idx) => savedSet.has(idx.toString()));
          setCheckedDocuments(restored.length === flatDocuments.length ? restored : initialState);
        } else {
          setCheckedDocuments(initialState);
        }
      }
    };

    loadState();
  }, [form, formId, isGrouped, groupedDocs.length, flatDocuments.length, currentLanguage]);

  // Auto-save on state change
  useEffect(() => {
    if (!form) return;

    const saveState = async () => {
      if (isGrouped) {
        const optionsMap = new Map<string, string>();
        selectedGroupIndices.forEach((indices, groupIdx) => {
          // Convert Set to comma-separated string
          const indicesStr = Array.from(indices).join(',');
          optionsMap.set(groupIdx.toString(), indicesStr);
        });

        await checklistService.saveChecklist(formId || '', {
          selectedDocuments: new Set(),
          selectedOptions: optionsMap,
          timestamp: Date.now()
        });
      } else {
        const selectedSet = new Set<string>();
        checkedDocuments.forEach((checked, idx) => {
          if (checked) selectedSet.add(idx.toString());
        });

        await checklistService.saveChecklist(formId || '', {
          selectedDocuments: selectedSet,
          selectedOptions: new Map(),
          timestamp: Date.now()
        });
      }
    };

    saveState();
  }, [selectedGroupIndices, checkedDocuments, formId, form, isGrouped]);

  // Calculate progress
  const getProgress = () => {
    if (isGrouped) {
      let selectedCount = 0;
      for (let i = 0; i < groupedDocs.length; i++) {
        const group = groupedDocs[i];
        const selectedIndices = selectedGroupIndices[i] || new Set();
        
        // Check if group has sub-documents or if the title itself is the document
        const hasSubDocuments = group.documents.length > 0;
        
        if (!hasSubDocuments) {
          // Title is the document - count if index 0 is selected
          if (selectedIndices.has(0)) selectedCount++;
        } else if (group.anyOne === true) {
          // For anyOne=true groups, count as complete if ANY ONE is selected
          if (selectedIndices.size > 0) selectedCount++;
        } else {
          // For anyOne=false or undefined, count as complete only if ALL documents are selected
          if (selectedIndices.size === group.documents.length) selectedCount++;
        }
      }
      const total = groupedDocs.length;
      return { current: selectedCount, total, percentage: total > 0 ? (selectedCount / total) * 100 : 0 };
    } else {
      const checkedCount = checkedDocuments.filter(checked => checked).length;
      const total = flatDocuments.length;
      return { current: checkedCount, total, percentage: total > 0 ? (checkedCount / total) * 100 : 0 };
    }
  };

  const progress = getProgress();
  const isComplete = progress.current === progress.total && progress.total > 0;

  // Show completion dialog when complete
  useEffect(() => {
    if (isComplete && !showCompletionDialog) {
      setShowCompletionDialog(true);
    }
  }, [isComplete]);

  // Handlers
  const handleGroupSelection = (groupIndex: number, docIndex: number) => {
    const group = groupedDocs[groupIndex];
    const newState = [...selectedGroupIndices];
    const selectedIndices = new Set(newState[groupIndex]);
    
    // Check if group has sub-documents
    const hasSubDocuments = group.documents.length > 0;

    if (!hasSubDocuments) {
      // Title is the document itself - toggle selection of index 0
      if (selectedIndices.has(0)) {
        selectedIndices.delete(0);
      } else {
        selectedIndices.clear();
        selectedIndices.add(0);
      }
    } else if (group.anyOne === true) {
      // Single selection mode (anyOne = true) - only one document can be selected at a time
      if (selectedIndices.has(docIndex)) {
        // Deselect if clicking the same item
        selectedIndices.delete(docIndex);
      } else {
        // Clear all previous selections and select only this one
        selectedIndices.clear();
        selectedIndices.add(docIndex);
      }
    } else {
      // Multi-select mode (anyOne = false or undefined) - allow selecting all documents
      if (selectedIndices.has(docIndex)) {
        selectedIndices.delete(docIndex);
      } else {
        selectedIndices.add(docIndex);
      }
    }

    newState[groupIndex] = selectedIndices;
    setSelectedGroupIndices(newState);
  };

  const handleCheckboxToggle = (docIndex: number) => {
    const newState = [...checkedDocuments];
    newState[docIndex] = !newState[docIndex];
    setCheckedDocuments(newState);
  };

  const handleReset = async () => {
    if (isGrouped) {
      setSelectedGroupIndices(Array.from({ length: groupedDocs.length }, () => new Set<number>()));
    } else {
      setCheckedDocuments(new Array(flatDocuments.length).fill(false));
    }
    await checklistService.clearChecklist(formId || '');
    setShowCompletionDialog(false);
  };

  const handleDone = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !form) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{t('error_occurred')}</Alert>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="md" sx={{ py: 4, pb: 10 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" fontWeight="bold" sx={{ flexGrow: 1, fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' } }}>
            {t('document_checklist')}
          </Typography>
          {progress.current > 0 && (
            <IconButton onClick={handleReset} color="error">
              <Refresh />
            </IconButton>
          )}
        </Box>

        {/* Progress Header */}
        <Box
          sx={{
            p: 3,
            mb: 3,
            bgcolor: isComplete ? '#E8F5E9' : '#F3E5F5',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              {progress.current} of {progress.total} {isGrouped ? 'categories' : 'documents'} completed
            </Typography>
            {isComplete && <CheckCircle sx={{ color: '#43A047', fontSize: 32 }} />}
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress.percentage}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: '#E0E0E0',
              '& .MuiLinearProgress-bar': {
                bgcolor: isComplete ? '#43A047' : '#673AB7',
                borderRadius: 4
              }
            }}
          />
        </Box>

        {/* Document List */}
        {isGrouped ? (
          // Grouped Documents (Radio/Checkbox Mode based on anyOne)
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {groupedDocs.map((group, groupIndex) => {
              const selectedIndices = selectedGroupIndices[groupIndex] || new Set<number>();
              
              // Check if group has sub-documents or if the title itself is the document
              const hasSubDocuments = group.documents.length > 0;
              const isTitleSelected = !hasSubDocuments && selectedIndices.has(0);
              
              // Determine if group is complete based on anyOne flag
              const isGroupComplete = !hasSubDocuments
                ? selectedIndices.has(0)  // Title-only: complete if title is selected
                : group.anyOne === true
                  ? selectedIndices.size > 0  // anyOne=true: complete if at least one is selected
                  : selectedIndices.size === group.documents.length;  // anyOne=false: complete if all are selected

              return (
                <Card
                  key={groupIndex}
                  sx={{
                    borderRadius: 3,
                    border: isGroupComplete ? '2px solid #4CAF50' : '1px solid #E0E0E0',
                    bgcolor: isGroupComplete ? '#E8F5E9' : 'white',
                    boxShadow: isGroupComplete ? '0 2px 8px rgba(76,175,80,0.2)' : '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  {/* Group Header */}
                  <Box 
                    sx={{ 
                      p: 2, 
                      bgcolor: isTitleSelected ? '#E8F5E9' : (isGroupComplete ? '#E8F5E9' : '#F5F5F5'),
                      cursor: !hasSubDocuments ? 'pointer' : 'default',
                      '&:hover': !hasSubDocuments ? {
                        bgcolor: isTitleSelected ? '#E8F5E9' : '#F0F0F0'
                      } : {}
                    }}
                    onClick={() => !hasSubDocuments && handleGroupSelection(groupIndex, 0)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {!hasSubDocuments ? (
                        <Radio
                          checked={isTitleSelected}
                          sx={{
                            color: '#9E9E9E',
                            '&.Mui-checked': {
                              color: '#4CAF50'
                            }
                          }}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            bgcolor: isGroupComplete ? '#4CAF50' : '#9E9E9E',
                            width: 32,
                            height: 32
                          }}
                        >
                          <Description fontSize="small" />
                        </Avatar>
                      )}
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
                        {group.type}
                      </Typography>
                      {isGroupComplete && hasSubDocuments && <CheckCircle sx={{ color: '#4CAF50' }} />}
                    </Box>
                  </Box>

                  {/* Documents in group - only show if hasSubDocuments */}
                  {hasSubDocuments && (
                    <Box sx={{ px: 2, pb: 2 }}>
                      {group.anyOne === true ? (
                        // Radio buttons for anyOne=true groups (single selection)
                        <RadioGroup
                          value={selectedIndices.size > 0 ? Array.from(selectedIndices)[0] : -1}
                          onChange={(e) => handleGroupSelection(groupIndex, parseInt(e.target.value))}
                        >
                          {group.documents.map((doc, docIndex) => {
                            const isDocSelected = selectedIndices.has(docIndex);
                            return (
                              <Box
                                key={docIndex}
                                sx={{
                                  py: 1.5,
                                  px: 1,
                                  borderRadius: 1,
                                  bgcolor: isDocSelected ? '#E8F5E9' : 'transparent',
                                  '&:hover': {
                                    bgcolor: isDocSelected ? '#E8F5E9' : '#F5F5F5'
                                  }
                                }}
                              >
                                <FormControlLabel
                                  value={docIndex}
                                  control={
                                    <Radio
                                      sx={{
                                        color: '#9E9E9E',
                                        '&.Mui-checked': {
                                          color: '#4CAF50'
                                        }
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography
                                      sx={{
                                        fontWeight: isDocSelected ? 600 : 400,
                                        color: isDocSelected ? '#2E7D32' : 'text.primary'
                                      }}
                                    >
                                      {doc}
                                    </Typography>
                                  }
                                  sx={{ m: 0, width: '100%' }}
                                />
                              </Box>
                            );
                          })}
                        </RadioGroup>
                      ) : (
                        // Checkboxes for anyOne=false or undefined groups (multi-selection - all required)
                        <Box>
                          {group.documents.map((doc, docIndex) => {
                            const isDocSelected = selectedIndices.has(docIndex);
                            return (
                              <Box
                                key={docIndex}
                                sx={{
                                  py: 1.5,
                                  px: 1,
                                  borderRadius: 1,
                                  bgcolor: isDocSelected ? '#E8F5E9' : 'transparent',
                                  '&:hover': {
                                    bgcolor: isDocSelected ? '#E8F5E9' : '#F5F5F5'
                                  }
                                }}
                              >
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isDocSelected}
                                      onChange={() => handleGroupSelection(groupIndex, docIndex)}
                                      sx={{
                                        color: '#9E9E9E',
                                        '&.Mui-checked': {
                                          color: '#4CAF50'
                                        }
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography
                                      sx={{
                                        fontWeight: isDocSelected ? 600 : 400,
                                        color: isDocSelected ? '#2E7D32' : 'text.primary'
                                      }}
                                    >
                                      {doc}
                                    </Typography>
                                  }
                                  sx={{ m: 0, width: '100%' }}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  )}
                </Card>
              );
            })}
          </Box>
        ) : (
          // Flat Documents (Checkbox Mode)
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {flatDocuments.map((doc, index) => {
              const isChecked = checkedDocuments[index] || false;
              return (
                <Card
                  key={index}
                  sx={{
                    borderRadius: 2,
                    border: isChecked ? '2px solid #4CAF50' : '1px solid #E0E0E0',
                    bgcolor: isChecked ? '#E8F5E9' : 'white'
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleCheckboxToggle(index)}
                          sx={{
                            color: '#9E9E9E',
                            '&.Mui-checked': {
                              color: '#4CAF50'
                            }
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontWeight: isChecked ? 600 : 400,
                            textDecoration: isChecked ? 'line-through' : 'none',
                            color: isChecked ? '#2E7D32' : 'text.primary'
                          }}
                        >
                          {doc}
                        </Typography>
                      }
                      sx={{ m: 0, width: '100%' }}
                    />
                  </Box>
                </Card>
              );
            })}
          </Box>
        )}

        {/* Completion Button */}
        {isComplete && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              bgcolor: 'white',
              boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
              zIndex: 1000
            }}
          >
            <Container maxWidth="md">
              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={<CheckCircle />}
                onClick={() => setShowCompletionDialog(true)}
                sx={{
                  bgcolor: '#43A047',
                  '&:hover': { bgcolor: '#388E3C' },
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2
                }}
              >
                {t('all_documents_ready')}
              </Button>
            </Container>
          </Box>
        )}
      </Container>

      {/* Completion Dialog */}
      <Dialog
        open={showCompletionDialog}
        onClose={() => setShowCompletionDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle sx={{ color: '#43A047', fontSize: 32 }} />
          {t('all_set')}
        </DialogTitle>
        <DialogContent>
          <Typography>{t('completion_message')}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowCompletionDialog(false)} sx={{ textTransform: 'none' }}>
            {t('stay_here')}
          </Button>
          <Button
            onClick={handleDone}
            variant="contained"
            sx={{
              bgcolor: '#673AB7',
              '&:hover': { bgcolor: '#5E35B1' },
              textTransform: 'none'
            }}
          >
            {t('done')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
